import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileText } from "lucide-react";
import { api, tzOffset } from "@/lib/api";
import PeriodPicker, { usePeriod, periodLabel } from "@/components/PeriodPicker";
import { useAuth } from "@/lib/auth";
import AddExpenseModal from "@/components/AddExpenseModal";
import ClosingReportModal from "@/components/ClosingReportModal";
import { ExpensesPanel } from "@/pages/Reports";

export default function Expenses() {
  const { outlet } = useOutletContext();
  const { role, userName } = useAuth();
  const [period, setPeriod] = usePeriod();
  const [report, setReport] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [closingOpen, setClosingOpen] = useState(false);

  const load = useCallback(() => {
    if (!outlet) return;
    api.get(`/reports/daily?outlet_id=${outlet.id}&date_from=${period.from}&date_to=${period.to}&tz_offset=${tzOffset()}`).then(({ data }) => setReport(data));
  }, [outlet, period]);

  useEffect(load, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Expense deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  if (!outlet || !report) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="readable h-full overflow-y-auto thin-scroll" data-testid="expenses-page">
      <div className="max-w-3xl mx-auto p-6 lg:p-8 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight">Expenses</h1>
            <p className="text-base text-muted-foreground mt-1" data-testid="expenses-period-label">{outlet.name} · {periodLabel(period)}</p>
          </div>
          <PeriodPicker value={period} onChange={setPeriod} testid="expenses-period" />
        </div>
        <div className="flex items-center justify-end gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="expenses-page-closing-btn" onClick={() => setClosingOpen(true)}
              className="flex items-center gap-2 border border-border bg-card px-3.5 py-2.5 text-sm font-semibold hover:bg-muted active:scale-[0.98] transition-transform">
              <FileText className="w-4 h-4" /> {period.from !== period.to ? "Period Summary" : "Day End Report"}
            </button>
            <button data-testid="expenses-page-add-btn" onClick={() => setAddOpen(true)}
              className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 text-sm font-bold hover:opacity-90 active:scale-[0.98] transition-transform">
              <Plus className="w-4 h-4" /> Add expense
            </button>
          </div>
        </div>
        <ExpensesPanel r={report} showDate={period.from !== period.to} canDelete={role === "owner"} onDelete={remove} onAdd={() => setAddOpen(true)} />
      </div>
      {addOpen && <AddExpenseModal outlet={outlet} onClose={() => setAddOpen(false)} onAdded={load} />}
      {closingOpen && <ClosingReportModal report={report} printedBy={userName} onClose={() => setClosingOpen(false)} />}
    </div>
  );
}
